import AdsContainer from "@components/Container/AdsContainer";
import {
  CompanionAdsLb,
  CompanionAdsLb1,
} from "@elements/Advertisement/variant/CompanionAdsLb";
import { getTagsName } from "@elements/GAData/GADataHelper";
import {
  CreateLoadMoreHandler,
  CreateLoadMoreHandlerProps,
} from "@elements/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@elements/LoadMore/LoadMore";
import Outbrain from "@elements/OutbrainAds/Outbrain";
import fetchBHApi from "@helper/fetchBHApi";
import { isArrayWithElements, isClientSide } from "@helper/utils";
import useVisibleArticle from "@hooks/useVisibleArticle";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import React, { useEffect, useState } from "react";

import ArticleContentCard from "./ArticleContentCard";
import ArticleContentCardNew from "./ArticleLayout/ArticleContentCardNew";
import ArticleElementWrapper from "./Components/ArticleElementWrapper";

type ArticleInfiniteScrollProps = {
  mainArticle: ProcessedArticleData;
  latestRegisteredArticle: ProcessedArticleData[];
  onNewArticle: (
    article: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => void;
  isOutbrainEnabled: boolean;
  PubPageAdTarget: AdTargetingType<ArticleAdTargetKeys, string>;
  newDesign?: boolean;
};

export default function ArticleInfiniteScroll({
  mainArticle,
  latestRegisteredArticle,
  onNewArticle,
  isOutbrainEnabled = false,
  PubPageAdTarget,
  newDesign,
}: ArticleInfiniteScrollProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>([mainArticle.id]),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [visibleArticleId, setVisibleArticleId] = useState<string>(
    mainArticle.id,
  );

  // Trigger useVisibleArticle when loadMoreData changes
  const visibleArticleIdFromHook = useVisibleArticle(visibleArticleId);
  const [currentPath, setCurrentPath] = useState<string>(
    mainArticle.urlPath || "",
  );

  const filterArticlesBasedOn = mainArticle.parentSection
    ? mainArticle.linkToSection.split("/").at(-1)
    : mainArticle.uniqueSectionName;

  const fetchNextStoryData = async (
    requestData: MoreArticlesRequestData,
  ): Promise<ProcessedArticleData[]> => {
    const queryParams = new URLSearchParams({
      section: requestData.filterArticlesBasedOn as string,
      excludeIds: requestData.excludeIds as string,
    }).toString();
    const response: ProcessedArticleData[] = await fetchBHApi(
      `get-article?${queryParams}`,
      "GET",
    );

    return response;
  };

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? mainArticle : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn,
      excludeIds: [...renderedArticleIds].join(","),
    };

    const fetchFunction: FetchMoreArticlesFunction = fetchNextStoryData;
    const updateGADataForArticle = true;

    const createLoadMoreProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      loadMoreData,
      renderedArticleIds,
      lastLoadedArticle,
      pageSize: 1,
      pageNumber,
      updateGADataForArticle,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreProps);

    if (isArrayWithElements(response)) {
      const article = response[0];
      // Update the URL and visibleArticleId when the article is loaded
      setCurrentPath(article.urlPath || "");
      setVisibleArticleId(article.id);
      onNewArticle(article, pageNumber + 1, false);
    } else {
      setHasMoreStories(false);
    }

    return response;
  };

  useEffect(() => {
    if (!isClientSide || !visibleArticleId) return;

    const allArticles = [mainArticle, ...loadMoreData];
    const visibleArticleIndex = allArticles.findIndex(
      (article) => article.id === visibleArticleId,
    );

    const articleData =
      visibleArticleIndex !== -1 ? allArticles[visibleArticleIndex] : undefined;

    if (articleData) {
      const newPath = articleData.urlPath;
      if (newPath && newPath !== currentPath) {
        window.history.pushState("article", articleData.title, newPath);
        setCurrentPath(newPath);
        onNewArticle(articleData, visibleArticleIndex, true); // Notify parent component
      }
    }
  }, [visibleArticleId, loadMoreData, mainArticle, onNewArticle, currentPath]);

  useEffect(() => {
    if (!isClientSide) return;
    if (visibleArticleIdFromHook) {
      setVisibleArticleId(visibleArticleIdFromHook);
    }
  }, [visibleArticleIdFromHook]);

  return (
    <div>
      {[...loadMoreData].map((context, index) => (
        <React.Fragment key={context.id}>
          {newDesign ? (
            <AdsContainer className="my-6 lg:my-8">
              <CompanionAdsLb1
                uniqueSectionName={context.adsUniqueSectionName}
                stripeBackground={false}
                index={index + 1}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                  PubPageAdTarget,
                ]}
              />
            </AdsContainer>
          ) : (
            <CompanionAdsLb
              uniqueSectionName={context.adsUniqueSectionName}
              index={index + 1}
              gsChannels={context.gsChannels}
              articleSlotTarget={[
                { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                {
                  key: ArticleAdTargetKeys.TAGS,
                  value: getTagsName(context.tags || []).toLowerCase(),
                },
                PubPageAdTarget,
              ]}
            />
          )}
          <div id={context.id} className="article-card">
            {newDesign ? (
              <>
                <ArticleContentCardNew
                  mainArticle={context}
                  latestRegisteredArticle={latestRegisteredArticle}
                  pageNumber={index + 1}
                  isInfiniteScrollArticle={true}
                  gsChannels={context.gsChannels}
                  articleSlotTarget={[
                    { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                    {
                      key: ArticleAdTargetKeys.TAGS,
                      value: getTagsName(context.tags || []).toLowerCase(),
                    },
                    PubPageAdTarget,
                  ]}
                  index={index}
                  visibleArticleId={visibleArticleId}
                />
                <ArticleElementWrapper>
                  {isOutbrainEnabled && context.urlPath && (
                    <div className="mt-4 lg:mt-6 border-t-2 border-blue-400">
                      <Outbrain pathName={context.urlPath} />
                    </div>
                  )}
                </ArticleElementWrapper>
              </>
            ) : (
              <ArticleContentCard
                mainArticle={context}
                latestRegisteredArticle={latestRegisteredArticle}
                pageNumber={index + 1}
                isInfiniteScrollArticle={true}
                isOutbrainEnabled={isOutbrainEnabled}
                gsChannels={context.gsChannels}
                articleSlotTarget={[
                  { key: ArticleAdTargetKeys.ARTICLE_ID, value: context.id },
                  {
                    key: ArticleAdTargetKeys.TAGS,
                    value: getTagsName(context.tags || []).toLowerCase(),
                  },
                  PubPageAdTarget,
                ]}
                index={index}
                visibleArticleId={visibleArticleId}
              />
            )}
          </div>
        </React.Fragment>
      ))}
      <ArticleElementWrapper className="mt-12 mb-6 lg:mt-20 lg:mb-10">
        <LoadMore
          rootClassName="my-4"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
          loadText="Lagi Cerita"
          isArticlePage={true}
          virtualPVTitle={mainArticle.title}
          pageNumber={pageNumber}
        />
      </ArticleElementWrapper>
    </div>
  );
}
